/**
 * Created: 2026-10-24
 * Initial commit: Export the 课程统计 page to an Excel file
 *
 * Builds the 课程统计 sheet with exceljs and downloads it: one row per
 * session, the lists of a cell (dates, students, slots) on separate lines,
 * money as numbers, and a totals row.
 */

import type { CoachingSessionStatsQuery } from '../gql/graphql'
import { getWeekdayText } from '../lib/dateUtils'

type StatRow = CoachingSessionStatsQuery['coachingSessionStats'][number]

export interface SessionStatsTotals {
  hours: number
  income: number
  courtHours: number
  slotsCost: number
}

const YUAN = { numFmt: '"¥"#,##0.00' }

export const formatHour = (h: number) => `${String(h).padStart(2, '0')}:00`
export const formatYuan = (v: string | number) => `¥${Number(v).toFixed(2)}`

/** "2026-10-05 周日 10:00-12:00", one line per date. */
export function timeLines(slots: StatRow['slots']): string[] {
  const byDate = new Map<string, number[]>()
  for (const s of slots) {
    byDate.set(s.date, [...(byDate.get(s.date) ?? []), s.hour])
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(
      ([date, hours]) =>
        `${date} ${getWeekdayText(date)} ${formatHour(
          Math.min(...hours),
        )}-${formatHour(Math.max(...hours) + 1)}`,
    )
}

/** Hours taught: distinct date-hours, however many courts each one uses. */
export function durationHours(slots: StatRow['slots']): number {
  return new Set(slots.map((s) => `${s.date}-${s.hour}`)).size
}

function attendeeLines(row: StatRow): string {
  return row.attendees
    .map((a) => {
      const heads = a.headcount > 1 ? ` (${a.headcount}人)` : ''
      const name = `${a.student.name ?? '-'}${heads}`
      const draws = a.draws.map((d) => `${d.hours}课时 × ${formatYuan(d.hourPrice)}`)
      return `${name}: ${draws.join(', ')}`
    })
    .join('\n')
}

function slotLines(row: StatRow): string {
  return row.slots
    .map(
      (s) =>
        `${formatHour(s.hour)} · ${s.court.name} · ${
          s.courtPrice == null ? '未定价' : formatYuan(s.courtPrice)
        }`,
    )
    .join('\n')
}

export async function exportSessionStats(
  rows: StatRow[],
  totals: SessionStatsTotals,
  fileName: string,
): Promise<void> {
  // Loaded on demand: exceljs is large and only needed on export.
  const { Workbook } = await import('exceljs')
  const workbook = new Workbook()
  const sheet = workbook.addWorksheet('课程统计', {
    views: [{ state: 'frozen', ySplit: 1 }],
  })
  sheet.columns = [
    { header: '#', key: 'index', width: 6 },
    { header: '上课时间', key: 'time', width: 26 },
    { header: '时长(小时)', key: 'duration', width: 11 },
    { header: '课程', key: 'course', width: 18 },
    { header: '教练', key: 'coach', width: 12 },
    { header: '学员 · 课时 × 课时单价', key: 'attendees', width: 40 },
    { header: '课程收入', key: 'income', width: 13, style: YUAN },
    { header: '场地使用', key: 'slots', width: 30 },
    { header: '场地成本', key: 'slotsCost', width: 13, style: YUAN },
  ]
  sheet.getRow(1).font = { bold: true }

  rows.forEach((row, i) => {
    sheet.addRow({
      index: i + 1,
      time: timeLines(row.slots).join('\n'),
      duration: durationHours(row.slots),
      course: row.course.name,
      coach: row.coach.name ?? '-',
      attendees: attendeeLines(row),
      income: Number(row.income),
      slots: slotLines(row),
      slotsCost: Number(row.slotsCost),
    })
  })

  const totalRow = sheet.addRow({
    index: '合计',
    time: `共 ${rows.length} 节`,
    duration: totals.hours,
    income: totals.income,
    slots: `${totals.courtHours} 场时`,
    slotsCost: totals.slotsCost,
  })
  totalRow.font = { bold: true }

  sheet.eachRow((row) => {
    row.alignment = { vertical: 'top', wrapText: true }
  })

  const buffer = await workbook.xlsx.writeBuffer()
  const url = URL.createObjectURL(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
  )
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  URL.revokeObjectURL(url)
}
